import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from "react";
import { z } from "zod";

const SCHEMA_VERSION = 3;
const STORAGE_KEY = `booking_flow_v${SCHEMA_VERSION}`;
const FLOW_STAGES = [
  "idle",
  "selectingRoom",
  "liveCheckPending",
  "readyForGuests",
  "readyForPayment",
  "completed",
];

const initialState = {
  version: SCHEMA_VERSION,
  flowStage: "idle",
  search: null,
  stay: null,
  hotelSummary: null,
  modifyContext: null,
  cartId: null,
  booking: null,
  liveCheck: null,
};

const ALLOWED_TRANSITIONS = {
  idle: ["selectingRoom", "readyForGuests"],
  selectingRoom: ["liveCheckPending", "readyForGuests", "idle"],
  liveCheckPending: ["readyForGuests", "selectingRoom"],
  readyForGuests: ["readyForPayment", "selectingRoom"],
  readyForPayment: ["completed", "readyForGuests", "selectingRoom"],
  completed: ["selectingRoom", "idle"],
};

const isValidTransition = (current, next) =>
  current === next || (ALLOWED_TRANSITIONS[current] ?? []).includes(next);

const msPerDay = 1000 * 60 * 60 * 24;
const safeDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const guestsSchema = z
  .object({
    adults: z.coerce.number().int().min(1).max(20).default(1),
    children: z.coerce.number().int().min(0).max(10).default(0),
    rooms: z.coerce.number().int().min(1).max(10).default(1),
    childAges: z.array(z.coerce.number().int().min(0).max(17)).max(10).default([]),
  })
  .superRefine((val, ctx) => {
    if (val.children > 0 && val.childAges.length !== val.children) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "childAges length must match children",
        path: ["childAges"],
      });
    }
  });

const staySchema = z.object({
  checkIn: z.string(),
  checkOut: z.string(),
  nights: z.coerce.number().int().positive().optional(),
  guests: guestsSchema,
});

const hotelSummarySchema = z
  .object({
    vendorId: z.union([z.string(), z.number()]).optional(),
    id: z.union([z.string(), z.number()]).optional(),
  })
  .passthrough();

const roomSchema = z.object({
  id: z.any(),
}).passthrough();

const rateSchema = z
  .object({
    code: z.any().optional(),
    rateId: z.any().optional(),
    id: z.any().optional(),
  })
  .passthrough();

const liveCheckSchema = z
  .object({
    receivedAt: z.string().optional(),
    validFor: z.string().optional(),
    data: z.any().optional(),
    raw: z.any().optional(),
  })
  .passthrough();

const bookingSchema = z
  .object({
    cartId: z.string().optional().nullable(),
    hotel: hotelSummarySchema,
    room: roomSchema,
    rate: rateSchema,
    stay: staySchema,
    guestDetails: z.any().nullable().optional(),
    payment: z.any().nullable().optional(),
    liveCheck: liveCheckSchema.optional(),
  })
  .passthrough();

const computeNights = (checkIn, checkOut, fallback = null) => {
  const start = safeDate(checkIn);
  const end = safeDate(checkOut);
  if (!start || !end) return fallback;
  const diff = Math.round((end - start) / msPerDay);
  return diff > 0 ? diff : fallback;
};

const normalizeStay = (raw) => {
  if (!raw) return null;
  const parsed = staySchema.safeParse({
    checkIn: raw.checkIn ?? raw.checkin,
    checkOut: raw.checkOut ?? raw.checkout,
    nights: raw.nights,
    guests: raw.guests ?? raw,
  });
  if (!parsed.success) return null;
  const value = parsed.data;
  const nights = computeNights(value.checkIn, value.checkOut, value.nights ?? null) ?? 1;
  return { ...value, nights };
};

const normalizeHotelSummary = (raw) => {
  if (!raw) return null;
  const parsed = hotelSummarySchema.safeParse(raw);
  if (!parsed.success) return null;
  const id = parsed.data.vendorId ?? parsed.data.id;
  if (!id) return null;
  return { ...raw, vendorId: id };
};

const liveCheckKey = ({ vendorId, stay, roomId, rateId }) =>
  [
    vendorId ?? "vendor",
    stay?.checkIn,
    stay?.checkOut,
    stay?.guests?.rooms,
    stay?.guests?.adults,
    stay?.guests?.children,
    stay?.guests?.childAges?.join("-") ?? "",
    roomId ?? "room",
    rateId ?? "rate",
  ]
    .map((v) => String(v ?? ""))
    .join("|");

const normalizeLiveCheck = (raw, stay, room, rate, vendorId) => {
  if (!raw || !stay || !room || !rate || !vendorId) return null;
  const parsed = liveCheckSchema.safeParse(raw);
  if (!parsed.success) return null;
  const key = liveCheckKey({
    vendorId,
    stay,
    roomId: room.id,
    rateId: rate.code ?? rate.rateId ?? rate.id,
  });
  return {
    receivedAt: parsed.data.receivedAt ?? new Date().toISOString(),
    validFor: parsed.data.validFor ?? key,
    data: parsed.data.data ?? parsed.data.raw ?? raw,
  };
};

const normalizeBooking = (raw, derived) => {
  if (!raw) return null;
  const stay = derived.stay ?? normalizeStay(raw.stay);
  const hotel = normalizeHotelSummary(raw.hotel ?? derived.hotelSummary);
  const room = raw.room ?? null;
  const rate = raw.rate ?? null;
  const draft = stay && hotel && room && rate ? { ...raw, stay, hotel, room, rate } : null;
  if (!draft) return null;
  const parsed = bookingSchema.safeParse(draft);
  if (!parsed.success) return null;

    const booking = {
      cartId: parsed.data.cartId ?? null,
      hotel,
      room,
      rate,
      stay,
      guestDetails: parsed.data.guestDetails ?? null,
      payment: parsed.data.payment ?? null,
      // Hotel-wide cheapest stay total computed on HotelDetailPage from the
      // availability response (intra-hotel min). Retained as a fallback
      // reference for the cheapest-rate policy.
      cheapestStayTotal:
        typeof parsed.data.cheapestStayTotal === 'number'
          ? parsed.data.cheapestStayTotal
          : null,
      // Cheapest stay total across every hotel visible in the traveler's
      // filtered search results. Computed on HotelSearchPage from the
      // per-hotel totalAfterTax the backend already emits, then threaded
      // through search state → HotelDetailPage → hold creation → here.
      // Primary reference for the cross-hotel cheapest-rate policy;
      // BookingPaymentPage prefers this over cheapestStayTotal when set.
      cheapestAcrossSearch:
        typeof parsed.data.cheapestAcrossSearch === 'number'
          ? parsed.data.cheapestAcrossSearch
          : null,
    };

  const lc = normalizeLiveCheck(parsed.data.liveCheck, stay, room, rate, hotel.vendorId);
  if (lc) {
    booking.liveCheck = lc;
  }
  return booking;
};

const hydrateFromStorage = () => {
  if (typeof window === "undefined") return initialState;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw);
    if (parsed.version !== SCHEMA_VERSION) return initialState;

    // Only persist lightweight search params and stay; never persist booking/cart/liveCheck/rooms.
    const stay = normalizeStay(parsed.stay);
    const flowStage = FLOW_STAGES.includes(parsed.flowStage) ? parsed.flowStage : "idle";
    const modifyContext = parsed.modifyContext ?? null;

    return {
      ...initialState,
      flowStage,
      search: parsed.search ?? null,
      stay,
      modifyContext,
    };
  } catch (e) {
    console.warn("Failed to hydrate booking flow from storage", e);
    return initialState;
  }
};

function bookingReducer(state, action) {
  switch (action.type) {
    case "hydrate":
      return action.payload ?? state;
    case "set-search":
      return { ...initialState, search: action.payload ?? null };
    case "set-stay": {
      const stay = normalizeStay(action.payload);
      if (!stay) return state;
      const key = state.liveCheck?.validFor ?? null;
      const currentKey = key && state.booking
        ? liveCheckKey({
            vendorId: state.booking.hotel?.vendorId ?? state.hotelSummary?.vendorId,
            stay,
            roomId: state.booking.room?.id,
            rateId: state.booking.rate?.code ?? state.booking.rate?.rateId,
          })
        : null;
      const shouldDropLiveCheck = key && currentKey && key !== currentKey;
      const stayChanged =
        !state.stay ||
        state.stay.checkIn !== stay.checkIn ||
        state.stay.checkOut !== stay.checkOut;
      return {
        ...state,
        stay,
        liveCheck: shouldDropLiveCheck ? null : state.liveCheck,
        booking: stayChanged ? null : state.booking,
        cartId: stayChanged ? null : state.cartId,
        flowStage: stayChanged ? "selectingRoom" : state.flowStage,
      };
    }
    case "set-hotel-summary": {
      const hotelSummary = normalizeHotelSummary(action.payload);
      if (!hotelSummary) return state;
      const lockedVendorId = state.modifyContext?.vendorId ?? state.modifyContext?.vendor_id ?? null;
      if (lockedVendorId) {
        const nextId = hotelSummary.vendorId ?? hotelSummary.id ?? null;
        if (nextId && String(nextId) !== String(lockedVendorId)) {
          return state;
        }
      }
      const currentId = state.hotelSummary?.vendorId ?? state.hotelSummary?.id;
      const nextId = hotelSummary.vendorId ?? hotelSummary.id;
      const changed = currentId && nextId && currentId !== nextId;
      return changed
        ? {
            ...state,
            hotelSummary,
            booking: null,
            liveCheck: null,
            cartId: null,
            flowStage: "selectingRoom",
          }
        : { ...state, hotelSummary };
    }
    case "set-cart-id": {
      const cartId = action.payload ?? null;
      return { ...state, cartId };
    }
    case "set-live-check": {
      const lc = normalizeLiveCheck(
        action.payload,
        state.stay,
        state.booking?.room ?? action.meta?.room,
        state.booking?.rate ?? action.meta?.rate,
        state.booking?.hotel?.vendorId ?? state.hotelSummary?.vendorId
      );
      if (!lc) return state;
      return { ...state, liveCheck: lc };
    }
    case "set-booking": {
      const booking = normalizeBooking(action.payload, {
        stay: state.stay,
        hotelSummary: state.hotelSummary,
      });
      if (!booking) return state;
      const nextStage = state.flowStage === "idle" ? "readyForGuests" : state.flowStage;
      return {
        ...state,
        booking,
        stay: booking.stay,
        hotelSummary: booking.hotel,
        cartId: booking.cartId ?? state.cartId ?? null,
        liveCheck: booking.liveCheck ?? state.liveCheck,
        flowStage: nextStage,
      };
    }
    case "set-flow-stage": {
      const next = action.payload;
      if (!FLOW_STAGES.includes(next) || !isValidTransition(state.flowStage, next)) {
        return state;
      }
      return { ...state, flowStage: next };
    }
    case "set-modify-context": {
      const modifyContext = action.payload ?? null;
      return { ...state, modifyContext };
    }
    case "clear-booking":
      return { ...state, booking: null, liveCheck: null, cartId: null, flowStage: "selectingRoom" };
    case "reset":
      return initialState;
    default:
      return state;
  }
}

export const BookingFlowContext = createContext(null);

export function BookingFlowProvider({ children }) {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  // Hydrate once on mount
  useEffect(() => {
    const hydrated = hydrateFromStorage();
    dispatch({ type: "hydrate", payload: hydrated });
  }, []);

  // Persist to sessionStorage
  useEffect(() => {
    try {
      const minimal = {
        version: state.version,
        flowStage: state.flowStage,
        search: state.search,
        stay: state.stay,
        modifyContext: state.modifyContext,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(minimal));
    } catch (e) {
      console.warn("Failed to persist booking flow state", e);
    }
  }, [state]);

  const setSearch = useCallback(
    (payload) => dispatch({ type: "set-search", payload }),
    []
  );
  const setStay = useCallback(
    (payload) => dispatch({ type: "set-stay", payload }),
    []
  );
  const setHotelSummary = useCallback(
    (payload) => dispatch({ type: "set-hotel-summary", payload }),
    []
  );
  const setCartId = useCallback(
    (payload) => dispatch({ type: "set-cart-id", payload }),
    []
  );
  const setLiveCheck = useCallback(
    (payload, meta = {}) => dispatch({ type: "set-live-check", payload, meta }),
    []
  );
  const setBooking = useCallback(
    (payload) => dispatch({ type: "set-booking", payload }),
    []
  );
  const setFlowStage = useCallback(
    (payload) => dispatch({ type: "set-flow-stage", payload }),
    []
  );
  const setModifyContext = useCallback(
    (payload) => dispatch({ type: "set-modify-context", payload }),
    []
  );
  const clearBooking = useCallback(() => dispatch({ type: "clear-booking" }), []);
  const resetFlow = useCallback(() => dispatch({ type: "reset" }), []);

  const selectors = useMemo(
    () => ({
      getNights: () => state.stay?.nights ?? null,
      getGuests: () => state.stay?.guests ?? null,
      getStay: () => state.stay ?? null,
      getBooking: () => state.booking ?? null,
      getLiveCheck: () => state.liveCheck ?? null,
      getModifyContext: () => state.modifyContext ?? null,
    }),
    [state.booking, state.liveCheck, state.modifyContext, state.stay]
  );

  const value = useMemo(
    () => ({
      state,
      setSearch,
      setStay,
      setHotelSummary,
      setCartId,
      setLiveCheck,
      setBooking,
      setFlowStage,
      setModifyContext,
      clearBooking,
      resetFlow,
      selectors,
    }),
    [
      state,
      setSearch,
      setStay,
      setHotelSummary,
      setCartId,
      setLiveCheck,
      setBooking,
      setFlowStage,
      setModifyContext,
      clearBooking,
      resetFlow,
      selectors,
    ]
  );

  return <BookingFlowContext.Provider value={value}>{children}</BookingFlowContext.Provider>;
}

export function useBookingFlow() {
  const context = useContext(BookingFlowContext);
  if (!context) {
    throw new Error("useBookingFlow must be used within a BookingFlowProvider");
  }
  return context;
}
